import * as React from 'react';

import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Per-SimpleTooltip default delay (ms). Tightened from the Radix default
// of 700 to feel less laggy for the common "what does this field do?"
// hover. Override per-call via the `delay` prop.
const DEFAULT_DELAY_MS = 500;
// Default hide delay (ms). Radix closes tooltips immediately on pointer
// leave; long-copy tooltips (marquee scroller, dense KV lists) need a
// beat so the reader can catch the tail before it disappears. Override
// per-call via the `hideDelay` prop; pass 0 to restore Radix's instant
// close.
const DEFAULT_HIDE_DELAY_MS = 0;

type TooltipContentProps = React.ComponentPropsWithoutRef<
  typeof TooltipContent
>;

/**
 * Width of the tooltip surface. Three values, because three are what the app
 * actually uses:
 *
 * - `'default'` — the shared `max-w-xs` cap. Long copy wraps instead of
 *   spanning the viewport as one line.
 * - `'narrow'` — a tighter 16rem cap for tooltips anchored inside a dense
 *   side panel, where the default cap overhangs the panel edge.
 * - `'content'` — no cap **and no surface padding**: the content element sets
 *   its own width and owns its own padding. This is one value rather than two
 *   props because the only reason to release the cap is that the child is
 *   already sizing itself — `MarqueeTooltipContent` pins an explicit pixel
 *   width and clips with `overflow-hidden`, so a padded frame would pinch the
 *   clipped edges and the cap would crop the pinned width.
 */
export type TooltipWidth = 'default' | 'narrow' | 'content';

// A plain lookup, not `cva`: this axis drives exactly one element (the content
// surface), which is the line src/components/AGENTS.md draws for reaching for
// cva at all.
//
// `!p-0` rather than `p-0`, carried over verbatim from the call sites this
// replaced. The padding it resets (`px-3 py-1.5`) lives in the vendored
// `TooltipContent` primitive. tailwind-merge treats the important modifier as
// a separate group, so `!p-0` does NOT drop `px-3 py-1.5` from the class
// string the way plain `p-0` would — it wins in the cascade instead. Same
// rendered box either way; keeping the important form means the reset also
// holds if a `pnpm dlx shadcn add` re-sync moves that padding somewhere twMerge
// no longer pairs with `p-*`.
const WIDTH_CLASS: Record<TooltipWidth, string> = {
  default: 'max-w-xs',
  narrow: 'max-w-[16rem]',
  content: 'max-w-none !p-0',
};

export interface SimpleTooltipProps {
  /**
   * Tooltip text/content. When nullish or an empty string the trigger renders
   * on its own with no tooltip — convenient for conditional labels.
   */
  content: React.ReactNode;
  /**
   * The element the tooltip is attached to. Must be a single element that
   * forwards ref + props (it becomes the `TooltipTrigger asChild`).
   */
  children: React.ReactElement;
  /** Which side of the trigger the tooltip appears on. */
  side?: TooltipContentProps['side'];
  /**
   * Width of the content surface. See {@link TooltipWidth}. Defaults to
   * `'default'`.
   */
  width?: TooltipWidth;
  /**
   * Render authored `\n` in `content` as line breaks
   * (`whitespace-pre-line`) instead of collapsing them
   * (`whitespace-normal`, the default).
   *
   * Not called `multiline`: the default already wraps long copy onto as many
   * lines as it needs. What this turns on is honouring the newlines the
   * *author* wrote — a per-face list, a "reason: …" block — which the default
   * would flatten into one paragraph.
   */
  preserveNewlines?: boolean;
  /**
   * When set, the trigger is wrapped in a `<span>` with these classes. Use this
   * for triggers that can be `disabled`: Radix tooltips don't open on a disabled
   * element (a disabled control emits no pointer events), so the hover target
   * must be an always-interactive wrapper. Pass classes that preserve the
   * trigger's layout slot (e.g. `"inline-flex"`, `"block w-full"`, `"flex flex-1"`),
   * and give the disabled child `disabled:pointer-events-none` so hover reaches
   * the span.
   */
  triggerWrapperClassName?: string;
  /**
   * Open delay in milliseconds. Defaults to `DEFAULT_DELAY_MS` (500). Each
   * SimpleTooltip runs its own isolated TooltipProvider so this delay
   * applies independently — sweeping the cursor across adjacent labels
   * does NOT trigger Radix's "skip" optimization that opens subsequent
   * tooltips instantly. Each one waits its full delay.
   */
  delay?: number;
  /**
   * Close delay in ms after pointer leaves the trigger. `0` (default)
   * matches Radix's instant close. Set to ~500 for long-copy tooltips
   * (e.g. `MarqueeTooltipContent`) so the reader has a beat to catch
   * the scroll tail before it disappears. If the pointer re-enters the
   * trigger during the delay, the pending close is cancelled.
   */
  hideDelay?: number;
}

/**
 * Thin wrapper over the shadcn `Tooltip` for the common "hover or focus this
 * element to see a short label" case. It is the canonical replacement for
 * native `title=` attributes, which are not keyboard- or touch-accessible and
 * have inconsistent screen-reader support.
 *
 * Relies on the global `<TooltipProvider>` mounted in `main.tsx`.
 *
 * Accessibility: a tooltip does **not** provide an accessible name (this is by
 * design in Radix — the tooltip is supplemental). For icon-only triggers, give
 * the trigger its own `aria-label` (or visible text) so screen readers announce
 * it. `SimpleTooltip` deliberately does not inject one, so it never clobbers a
 * trigger that already has a label.
 *
 * Disabled triggers: a disabled control emits no pointer events, so the tooltip
 * won't open on it. For those, pass `triggerWrapperClassName` (see above).
 *
 * Styling: the content surface takes typed props (`width`,
 * `preserveNewlines`), not a free-form `className`. The three widths and the
 * newline override are every distinct surface this app renders; anything else
 * belongs in the content node itself (see `MarqueeTooltipContent`).
 */
export function SimpleTooltip({
  content,
  children,
  side,
  width = 'default',
  preserveNewlines = false,
  triggerWrapperClassName,
  delay = DEFAULT_DELAY_MS,
  hideDelay = DEFAULT_HIDE_DELAY_MS,
}: SimpleTooltipProps) {
  const trigger = triggerWrapperClassName ? (
    <span className={triggerWrapperClassName}>{children}</span>
  ) : (
    children
  );

  // Controlled open state ONLY when a hide-delay is requested. In the
  // common `hideDelay === 0` path we hand `open`/`onOpenChange` back
  // to Radix (uncontrolled) so the default open/close/focus/blur/touch
  // behavior stays unchanged for the ~all-other tooltips on the page.
  const [openState, setOpenState] = React.useState(false);
  const closeTimerRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const handleOpenChange = (next: boolean) => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (next) {
      setOpenState(true);
      return;
    }
    // Pointer left the trigger — defer the close so the reader has
    // time to finish the marquee/long copy. A re-enter (`next === true`
    // above) cancels the pending close via the clearTimeout branch,
    // so a bounce off the edge of the trigger doesn't dismiss the pill.
    closeTimerRef.current = window.setTimeout(() => {
      setOpenState(false);
      closeTimerRef.current = null;
    }, hideDelay);
  };

  if (content == null || content === '') {
    return trigger;
  }

  const controlled = hideDelay > 0;

  // Each SimpleTooltip has its own TooltipProvider so its open/close
  // lifecycle is isolated from other SimpleTooltips on the screen. With
  // the app-level shared TooltipProvider, sweeping the cursor across
  // adjacent triggers would let Radix's `skipDelayDuration` window open
  // each subsequent tooltip instantly — the "tooltips chase the cursor"
  // bug users reported on the mesh-settings form. Per-instance providers
  // bypass the shared skip state cleanly; each tooltip waits its full
  // `delayDuration`. Cost is one React context per tooltip, which is
  // negligible.
  return (
    <TooltipProvider delayDuration={delay}>
      <Tooltip
        {...(controlled
          ? { open: openState, onOpenChange: handleOpenChange }
          : {})}
      >
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        {/* Width cap + wrap so long-copy tooltips (localized glossary
            strings, multi-sentence explanations) don't span the whole
            viewport as one line. `whitespace-pre-line` and
            `whitespace-normal` are the same tailwind-merge conflict group,
            so the two are mutually exclusive by construction. */}
        <TooltipContent
          side={side}
          className={cn(
            'break-words',
            WIDTH_CLASS[width],
            preserveNewlines ? 'whitespace-pre-line' : 'whitespace-normal',
          )}
        >
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
